import { useEffect, useId, useRef, useState } from 'react';

interface TagSearchInputProps {
  value: string;
  onChange: (next: string) => void;
  /** 頻度降順のユニークなタグ一覧を想定。 */
  availableTags: string[];
  placeholder?: string;
  /** label に渡すクラス名。既存の "explore-bar__search" / "hero__search" などを想定。 */
  className?: string;
  showIcon?: boolean;
}

/** キャレット位置を含むトークンの範囲。 */
interface TokenRange {
  startIdx: number;
  endIdx: number;
  token: string;
}

/** 入力値とキャレット位置から、キャレットが乗っているトークン（空白区切り）の範囲を求める。 */
function getTokenAtCaret(value: string, caret: number): TokenRange {
  const leftPart = value.slice(0, caret);
  const startMatch = leftPart.search(/[\s　][^\s　]*$/);
  const startIdx = startMatch === -1 ? 0 : startMatch + 1;
  const rest = value.slice(caret);
  const rightMatch = rest.match(/^[^\s　]*/);
  const endIdx = caret + (rightMatch ? rightMatch[0].length : 0);
  return { startIdx, endIdx, token: value.slice(startIdx, endIdx) };
}

/** prefix に一致する部分を <mark> でハイライトする（大小文字は無視、表示は元表記）。 */
function highlightMatch(tag: string, prefix: string) {
  if (!prefix) return tag;
  const idx = tag.toLowerCase().indexOf(prefix.toLowerCase());
  if (idx === -1) return tag;
  return (
    <>
      {tag.slice(0, idx)}
      <mark>{tag.slice(idx, idx + prefix.length)}</mark>
      {tag.slice(idx + prefix.length)}
    </>
  );
}

export default function TagSearchInput({
  value,
  onChange,
  availableTags,
  placeholder = 'タイトル・作者・#タグ で検索',
  className,
  showIcon = true,
}: TagSearchInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLLabelElement>(null);
  const idPrefix = useId();

  const [open, setOpen] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [prefix, setPrefix] = useState('');
  const [tokenRange, setTokenRange] = useState<TokenRange | null>(null);
  const [highlightIndex, setHighlightIndex] = useState(0);

  // 外側クリックで閉じる。
  useEffect(() => {
    const handleMouseDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, []);

  /** 現在のキャレット位置からトークンを求め、# タグならサジェストを開く。 */
  const syncSuggestions = () => {
    const input = inputRef.current;
    if (!input) return;
    const caret = input.selectionStart ?? input.value.length;
    const range = getTokenAtCaret(input.value, caret);

    if (!range.token.startsWith('#')) {
      setOpen(false);
      return;
    }

    const currentPrefix = range.token.slice(1);
    const matched =
      currentPrefix.length === 0
        ? availableTags.slice(0, 8)
        : availableTags
            .filter((t) => t.toLowerCase().includes(currentPrefix.toLowerCase()))
            .slice(0, 8);

    setTokenRange(range);
    setPrefix(currentPrefix);
    setSuggestions(matched);
    setHighlightIndex(0);
    setOpen(true);
  };

  /** 候補を選び、入力値のトークンを #タグ名 に置換して確定する。 */
  const commitSuggestion = (tag: string) => {
    const input = inputRef.current;
    if (!input || !tokenRange) return;

    const replaced = `#${tag}`;
    const next = value.slice(0, tokenRange.startIdx) + replaced + ' ' + value.slice(tokenRange.endIdx);
    const nextCaret = tokenRange.startIdx + replaced.length + 1;

    onChange(next);
    setOpen(false);
    requestAnimationFrame(() => {
      input.setSelectionRange(nextCaret, nextCaret);
      input.focus();
    });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open || suggestions.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex((i) => (i + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex((i) => (i - 1 + suggestions.length) % suggestions.length);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      commitSuggestion(suggestions[highlightIndex]);
    } else if (e.key === 'Tab') {
      commitSuggestion(suggestions[highlightIndex]);
    } else if (e.key === 'Escape') {
      // type="search" の標準機能で入力欄がクリアされるのを防ぎ、閉じるだけにする。
      e.preventDefault();
      setOpen(false);
    }
  };

  const activeOptionId = open && suggestions.length > 0 ? `${idPrefix}-option-${highlightIndex}` : undefined;

  return (
    <label className={className} ref={containerRef}>
      {showIcon && (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
          <path d="M21 21l-4.35-4.35" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
      )}
      <input
        ref={inputRef}
        type="search"
        aria-label={placeholder}
        placeholder={placeholder}
        value={value}
        aria-activedescendant={activeOptionId}
        onChange={(e) => {
          onChange(e.target.value);
          syncSuggestions();
        }}
        onKeyUp={(e) => {
          if (['ArrowDown', 'ArrowUp', 'Enter', 'Tab', 'Escape'].includes(e.key)) return;
          syncSuggestions();
        }}
        onSelect={syncSuggestions}
        onKeyDown={handleKeyDown}
      />
      {open && (
        <ul className="tag-suggest" role="listbox">
          {suggestions.length === 0 && <li className="tag-suggest__empty">該当タグなし</li>}
          {suggestions.map((tag, index) => (
            <li
              key={tag}
              id={`${idPrefix}-option-${index}`}
              role="option"
              aria-selected={index === highlightIndex}
              className={`tag-suggest__item${index === highlightIndex ? ' on' : ''}`}
              onMouseDown={(e) => {
                // input の blur / selection 消失より先に確定させる。
                e.preventDefault();
                commitSuggestion(tag);
              }}
              onMouseEnter={() => setHighlightIndex(index)}
            >
              #{highlightMatch(tag, prefix)}
            </li>
          ))}
        </ul>
      )}
    </label>
  );
}
